import { Conditions, Formula, type LuachDay, type LuachEntry } from '@app/luach'

/**
 * One printed line, explained.
 *
 * Every part of this is the engine's own answer, rendered by the components the
 * rules editor and the app's logic view already use — `<Formula>` and
 * `<Conditions>`. Nothing is restated in marketing prose, which is the point: if
 * this panel could word a rule differently from the app, the page would be
 * advertising something the product does not quite do.
 *
 * The order is an argument. A reader's question is "where did that number come
 * from", so the answer runs: what it hangs off, what that anchor actually was
 * today, what put the line on THIS sheet, and only then why the shul wants it
 * that way. Reason last, because it is the part that persuades and it lands
 * better once the mechanics are already believed.
 */
export function LineLogic({
  entry,
  day,
}: {
  entry: LuachEntry
  day: LuachDay
}) {
  const f = entry.formula
  const conditions = [
    { key: 'Only on', values: entry.requireFlags },
    { key: 'Except on', values: entry.excludeFlags },
    ...(entry.conditionLabel
      ? [{ key: 'Only when', values: [entry.conditionLabel] }]
      : []),
  ]
  const gated = conditions.some((c) => c.values.length)

  return (
    <div className="mk-logic">
      <p className="mk-label">The rule behind it</p>
      <p className="mk-logic__name">
        {entry.labelEn}
        <span className="mk-logic__he" dir="rtl">
          {entry.labelHe}
        </span>
      </p>

      <div className="mk-logic__formula">
        <Formula spec={f} />
      </div>

      {/*
       * The anchor's own time. This is the line that turns the formula from a
       * claim into arithmetic a reader can do in their head — "shkia was 4:46,
       * so 40 before it is 4:06" — and it is why the resolver carries
       * `anchorTime` at all.
       */}
      {f.anchor && f.anchorTime ? (
        <dl className="mk-logic__rows">
          <div>
            <dt>{f.anchor} today</dt>
            <dd className="mk-num">{f.anchorTime}</dd>
          </div>
          {entry.time ? (
            <div>
              <dt>so this line is</dt>
              <dd className="mk-num mk-strong">{entry.time}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      {/* What put it on this sheet rather than another. A line with no gates is
          a line that prints every time — worth saying, because a reader
          otherwise assumes the blank means we could not tell them. */}
      <div className="mk-logic__gates">
        <p className="mk-label">On this sheet because</p>
        {gated ? (
          <Conditions conditions={conditions} />
        ) : (
          <p className="mk-logic__ungated">
            Nothing gates it — this one prints every time.
          </p>
        )}
        {day.flags.length ? (
          <p className="mk-logic__day">This day is {day.flags.join(' · ')}</p>
        ) : null}
      </div>

      {entry.rationale ? (
        <div className="mk-logic__why">
          <p className="mk-label">Why the shul times it so</p>
          <p className="mk-logic__reason">{entry.rationale}</p>
        </div>
      ) : null}
    </div>
  )
}
